// src/services/hospitalOrganizationService.js
// Service for subscription-based hospital organizations
// Separate from organizationService.js which handles corporate/insurance/government organizations
import axios from "axios";
import { BACKEND_URL } from "../utils/envConfig";

const API_URL = `${BACKEND_URL}/hospital-organizations`;

// Hospital Organization Management (for subscription-based organizations)
export const getAllHospitalOrganizations = async () => {
  const response = await axios.get(API_URL);
  return response.data;
};

export const getHospitalOrganizationById = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

export const createHospitalOrganization = async (organizationData) => {
  try {
    const response = await axios.post(API_URL, organizationData);
    return response.data;
  } catch (error) {
    // Log full error for debugging
    console.error("Hospital organization creation error:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error status:", error.response?.status);
    console.error("Request payload:", organizationData);
    
    // Re-throw with enhanced error message
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error ||
                        (typeof error.response?.data === 'string' ? error.response?.data : null) ||
                        error.message;
    
    const enhancedError = new Error(errorMessage || "Failed to create hospital organization");
    enhancedError.response = error.response;
    enhancedError.status = error.response?.status;
    throw enhancedError;
  }
};

export const updateHospitalOrganization = async (id, organizationData) => {
  const response = await axios.put(`${API_URL}/${id}`, organizationData);
  return response.data;
};

// Update hospital organization status (active/inactive)
export const updateHospitalOrganizationStatus = async (id, isActive) => {
  const response = await axios.put(`${API_URL}/${id}/status`, { isActive });
  return response.data;
};

export const deleteHospitalOrganization = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

// Create admin user for hospital organization
export const createHospitalAdminUser = async (organizationId, adminData) => {
  const response = await axios.post(`${API_URL}/${organizationId}/admin`, adminData);
  return response.data;
};

// Get admin user for hospital organization (optional endpoint — returns null if unavailable)
export const getHospitalOrganizationAdmin = async (organizationId) => {
  try {
    const response = await axios.get(`${API_URL}/${organizationId}/admin`);
    return response.data;
  } catch (error) {
    if (error.response?.status !== 404) {
      console.warn(
        '[getHospitalOrganizationAdmin] Admin endpoint unavailable:',
        error.message
      );
    }
    return null;
  }
};

// Update admin user for hospital organization
export const updateHospitalOrganizationAdmin = async (organizationId, adminData) => {
  const response = await axios.put(`${API_URL}/${organizationId}/admin`, adminData);
  return response.data;
};

const parseHospitalExistsFlag = (data) =>
  data?.exists === true || data === true;

// Check if hospital email already exists
export const checkHospitalEmailExists = async (email, excludeId = null) => {
  try {
    if (!email || !String(email).trim()) {
      return false;
    }

    const response = await axios.get(`${API_URL}/check-email`, {
      params: {
        email: String(email).trim(),
        ...(excludeId != null ? { excludeId } : {}),
      },
      validateStatus: (status) => status < 600,
    });

    return parseHospitalExistsFlag(response.data);
  } catch (error) {
    if (error.response?.data != null) {
      return parseHospitalExistsFlag(error.response.data);
    }
    return false;
  }
};

// Normalize hospital name for duplicate comparison
const normalizeHospitalName = (name) =>
  String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// Check if hospital organization name already exists
export const checkHospitalNameExists = async (name, excludeId = null) => {
  try {
    if (!name || !String(name).trim()) {
      return false;
    }

    const normalizedName = normalizeHospitalName(name);
    if (!normalizedName) {
      return false;
    }

    const response = await axios.get(`${API_URL}/check-name`, {
      params: {
        name: String(name).trim(),
        ...(excludeId != null ? { excludeId } : {}),
      },
      validateStatus: (status) => status < 600,
    });

    if (response.status < 400) {
      return parseHospitalExistsFlag(response.data);
    }

    const organizations = await getAllHospitalOrganizations();
    const list = Array.isArray(organizations) ? organizations : [];
    return list.some((org) => {
      if (excludeId != null && String(org.id) === String(excludeId)) {
        return false;
      }
      return normalizeHospitalName(org.name) === normalizedName;
    });
  } catch (error) {
    console.warn("Error checking hospital name, skipping duplicate check:", error.message);
    return false;
  }
};

// Check if hospital phone already exists
export const checkHospitalPhoneExists = async (phone, excludeId = null) => {
  try {
    if (!phone || !String(phone).trim()) {
      return false;
    }

    const response = await axios.get(`${API_URL}/check-phone`, {
      params: {
        phone: String(phone).trim().replace(/\D/g, ''),
        ...(excludeId != null ? { excludeId } : {}),
      },
      validateStatus: (status) => status < 600,
    });

    return parseHospitalExistsFlag(response.data);
  } catch (error) {
    if (error.response?.data != null) {
      return parseHospitalExistsFlag(error.response.data);
    }
    return false;
  }
};

export default {
  getAllHospitalOrganizations,
  getHospitalOrganizationById,
  createHospitalOrganization,
  updateHospitalOrganization,
  updateHospitalOrganizationStatus,
  deleteHospitalOrganization,
  createHospitalAdminUser,
  getHospitalOrganizationAdmin,
  updateHospitalOrganizationAdmin,
  checkHospitalEmailExists,
  checkHospitalPhoneExists,
  checkHospitalNameExists,
};

